import { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { PageHeader } from "../components/Section";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Heart } from "lucide-react";

const PRESETS = [25, 50, 100, 250, 500];
const PURPOSES = ["General Offering", "Tithe", "Building Fund", "Missions", "Benevolence"];

export default function Give() {
  const [amount, setAmount] = useState(50);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [purpose, setPurpose] = useState(PURPOSES[0]);
  const [loading, setLoading] = useState(false);

  const donate = async (e) => {
    e.preventDefault();
    if (!amount || amount < 1) { toast.error("Please enter an amount of at least $1"); return; }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/donations/checkout", {
        amount: parseFloat(amount), origin_url: origin,
        donor_name: name, donor_email: email, purpose,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div data-testid="give-page">
      <PageHeader eyebrow="GIVE AN OFFERING" title="Partner with the Council"
        subtitle="Every gift sustains the ministry, the prayer centers, and the outreach of the assembly."
        bg="https://images.unsplash.com/photo-1513346940221-6f673d962e97?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80" />
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-6 lg:px-10">
          <form onSubmit={donate} className="border hairline rounded-2xl p-8 surface space-y-6" data-testid="give-form">
            <div className="flex items-center gap-3 mb-2">
              <Heart className="w-5 h-5 text-gold" />
              <span className="font-accent text-xs text-gold">SECURE ONE-TIME GIFT</span>
            </div>

            <div>
              <Label className="text-zinc-300">Choose amount (USD)</Label>
              <div className="mt-3 grid grid-cols-5 gap-2">
                {PRESETS.map((v) => (
                  <button
                    key={v} type="button"
                    data-testid={`give-preset-${v}`}
                    onClick={() => setAmount(v)}
                    className={`py-2.5 rounded-lg border text-sm font-medium transition-colors duration-200 ${
                      amount === v ? "bg-gold text-black border-gold" : "bg-black/40 text-zinc-300 border-zinc-800 hover:border-gold/50"
                    }`}
                  >${v}</button>
                ))}
              </div>
              <Input
                type="number" min={1} step="0.01"
                data-testid="give-amount"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                className="mt-3 bg-black/40 border-zinc-800 text-lg font-display"
                placeholder="Custom amount"
              />
            </div>

            <div>
              <Label className="text-zinc-300">Designation</Label>
              <select
                data-testid="give-purpose"
                value={purpose} onChange={(e) => setPurpose(e.target.value)}
                className="mt-2 w-full bg-black/40 border border-zinc-800 rounded-md px-3 py-2 text-white text-sm focus:border-gold focus:outline-none"
              >
                {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Name (optional)</Label>
                <Input data-testid="give-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-2 bg-black/40 border-zinc-800" />
              </div>
              <div>
                <Label className="text-zinc-300">Email (optional)</Label>
                <Input type="email" data-testid="give-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-black/40 border-zinc-800" />
              </div>
            </div>

            <Button type="submit" disabled={loading} data-testid="give-submit" className="w-full bg-gold text-black hover:bg-slate-200 font-semibold py-6 text-base">
              {loading ? "Redirecting to secure checkout…" : `Give $${Number(amount || 0).toFixed(2)}`}
            </Button>
            <p className="text-xs text-zinc-500 text-center">Powered by Stripe · Secure & encrypted</p>
          </form>
        </div>
      </section>
    </div>
  );
}
